import React, { useMemo } from 'react'

import Separator from '../../../../../components/pdf/Separator'
import Text, { Bold } from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Bullets, Paragraph, SectionHeading, SubHeading, SubSubHeading } from './_shared/Prose'
import { RedDataCell, RedHeadCell, RedRow, RedTable } from './_shared/BradescoTable'
import { listarSetoresComCargos } from '../_helpers/cargoEngine'

/**
 * Seção 5 - Metodologia. Texto literal do modelo aprovado por Julia Buzzo ("Modelo PGR
 * 2026 - Banco Bradesco_Varejo"). Substitui o placeholder institucional anterior
 * (arquivo era `05.EstruturaDocumentalSection.js` — não há seção "Estrutura Documental"
 * no modelo real).
 */

/**
 * Tabela "Setor | Cargo (GHE)" da seção 5.3 — Julia Buzzo rejeitou a versão anterior
 * desta tabela (PR #728, commit fa00b84), que reproduzia como conteúdo FIXO o exemplo
 * de setores/cargos do PDF-modelo ("essa lista tem que ser preenchida de acordo com as
 * respostas das atividades").
 *
 * HISTÓRICO RESUMIDO da investigação Setor↔Cargo (4 rodadas, 2026-08-30 a 2026-09-01 —
 * mantido aqui para quem revisitar esta decisão; detalhe completo no histórico de
 * commits/PR, não repetido linha a linha):
 *   1ª rodada: hipótese de tabela FIXA Setor->Cargo (baseada no exemplo do PDF-modelo,
 *     página 18) — DESCARTADA pela própria Julia: "O sistema precisa considerar as
 *     respostas preenchidas nessas perguntas e, a partir delas, identificar quais
 *     setores e cargos devem ser utilizados" (não vai fornecer essa lista).
 *   2ª rodada: hipótese de `blockIndex` compartilhado entre Setor (256) e Cargo (mesmo
 *     mecanismo de `mountAssetsValues`, `pdfs/vivo/_helpers/pdfHelpers.js`) — DESCARTADA
 *     pela Julia: "Setor não é filha do mesmo bloco".
 *   3ª rodada: hipótese de grupo/categoria por OPÇÃO da pergunta de Cargo (na definição
 *     da checklist) — DESCARTADA por inspeção de código: não existe esse campo em
 *     `Question.options` neste app (query do builder só tem `{id,name,reference,
 *     ordination}`; UI de edição de opção só tem um input de `name`; "global options"
 *     semi-documentada/morta não carrega semântica de grupo).
 *   4ª rodada (RESOLVIDA): Julia enviou vídeo da tela real do builder/preview do
 *     checklist do Bradesco. Confirmado por inspeção direta: Cargo está dentro de um
 *     bloco "Características do cargo" que é ele mesmo uma pergunta condicionada a uma
 *     opção específica de Setor — cada Setor tem sua própria pergunta-marcador "Tire foto
 *     de cada posto de trabalho do setor [NOME DO SETOR]", imediatamente seguida, na
 *     ORDEM de exibição da checklist, pelo(s) bloco(s) de Cargo daquele Setor (visto ao
 *     vivo: bloco 682/Cargo 662 logo após a pergunta 257 "...[Gerência Agência]"; bloco
 *     791/Cargo ~792 logo após a pergunta 263 "...[Retaguarda Administrativa]"). O
 *     dropdown de Cargo em si é a MESMA lista completa em qualquer posição — o vínculo
 *     está na ORDEM/ESTRUTURA da checklist, nunca no nome do Cargo escolhido.
 *
 * FIX (ver `_helpers/cargoEngine.js`, `getSetorPorPosicaoDeCargo`/
 * `listarSetoresComCargos`): a tabela agora tem UMA LINHA POR SETOR (não uma linha única
 * com todos os Setores concatenados por vírgula numa célula e todos os Cargos na outra) —
 * cada linha lista só os Cargos cuja posição, na ordem real de exibição da atividade,
 * ficou "sob" aquele Setor (pergunta-marcador mais recente antes dela). Fallback (nunca
 * regride): se nenhuma posição resolver um Setor estrutural, volta ao comportamento
 * anterior (uma linha só, Setor global + todos os Cargos).
 */
const MetodologiaSection = ({ serviceOrder, assets }) => {
  const setoresCargosGhe = useMemo(
    () => listarSetoresComCargos(serviceOrder.replies, assets),
    [serviceOrder.replies, assets]
  )

  return (
    <View id="5">
      <Separator weight={15} />
      <SectionHeading numero={5}>METODOLOGIA</SectionHeading>

      <SubHeading id="5.1">5.1. Identificação e Avaliação dos Riscos Ocupacionais</SubHeading>
      <Paragraph>
        A elaboração deste Programa de Gerenciamento de Riscos (PGR) foi conduzida com base nas diretrizes estabelecidas
        pela Norma Regulamentadora nº 01 – Disposições Gerais e Gerenciamento de Riscos Ocupacionais, sendo
        complementada pelas seguintes normas:
      </Paragraph>
      <Bullets
        items={[
          'NR-09 – Avaliação e Controle das Exposições Ocupacionais a Agentes Físicos, Químicos e Biológicos;',
          'NR-17 – Ergonomia;',
          'NR-07 – Programa de Controle Médico de Saúde Ocupacional (PCMSO).'
        ]}
      />

      <Paragraph>
        A identificação e avaliação dos riscos ocupacionais foram realizadas por meio de inspeções técnicas em campo,
        cujos resultados estão consolidados no Inventário de Riscos Ocupacionais e no respectivo Plano de Ação, conforme
        os Anexos 17.1.3 e 17.1.4 da NR-01. Esses documentos subsidiam a análise crítica dos riscos e a definição de
        medidas de controle.
      </Paragraph>

      <Paragraph>
        O ciclo de vigência do PGR é de dois anos, ao término do qual será realizada uma nova avaliação para atualização
        do programa, reiniciando o processo de gestão de riscos.
      </Paragraph>

      <Paragraph>Para a avaliação dos riscos ocupacionais, foram adotadas duas ferramentas metodológicas:</Paragraph>
      <Bullets
        items={[
          'AIHA (American Industrial Hygiene Association): utilizada para a graduação dos riscos físicos, químicos, biológicos, ergonômicos e de acidentes. Essa metodologia permite a classificação dos agentes de risco presentes nos ambientes de trabalho, orientando a priorização e definição de medidas de controle.',
          'ERGOCHECK–PREMAPA: ferramenta desenvolvida pela International Ergonomics School (EPM), com base em fundamentos científicos e normativos, aplicada para a identificação e análise de riscos psicossociais. Permite a graduação dos resultados, o monitoramento contínuo e a definição de ações de controle, mitigação, minimização e eliminação.'
        ]}
      />

      <Paragraph>
        A metodologia adotada está plenamente alinhada aos requisitos da NR-01 e é compatível com os princípios da Norma
        ISO 45003:2021, que trata da gestão de riscos psicossociais no ambiente de trabalho.
      </Paragraph>

      <SubHeading id="5.2">5.2. Riscos Ocupacionais Avaliados</SubHeading>
      <Bullets
        items={[
          'Riscos ocupacionais físicos: Referem-se às condições ambientais relacionadas, principalmente, aos níveis de exposição à temperatura ambiente e ao ruído contínuo ou intermitente. A avaliação desses riscos segue as diretrizes estabelecidas na Norma Regulamentadora nº 09 (NR-09), que trata da Avaliação e Controle das Exposições Ocupacionais a Agentes Físicos, Químicos e Biológicos. Por se tratar de agência bancária com atividade exclusivamente administrativa, são utilizados como parâmetros de conforto a NR.17 Ergonomia, item: 17.8. Condições de Conforto no Ambiente de Trabalho, subitens: 17.8.4 e 17.8.4.2., que leva em considerações os requisitos mínimos para um ambiente confortável e saudável.',
          'Riscos ocupacionais biológicos: Aplicam-se exclusivamente a serviços de manutenção, conservação e limpeza dos sanitários, realizados por colaboradores contratados (terceiros), que utilizam adequadamente os Equipamentos de Proteção Individual (EPIs), como botas e luvas, os quais minimizam a exposição aos agentes biológicos presentes nesses ambientes.',
          'Riscos ocupacionais químicos: Referem-se à identificação de possíveis exposições a substâncias químicas presentes nos produtos utilizados por colaboradores contratados para a limpeza de equipamentos, mobiliários, pisos e sanitários.',
          'Riscos ocupacionais ergonômicos ambientais: Aplicam-se às condições ergonômicas do ambiente de trabalho. A avaliação contempla a análise do mobiliário (cadeiras e mesas), dos equipamentos utilizados nas atividades, da organização do trabalho, bem como das condições ambientais, como iluminação, temperatura e níveis de ruído de conforto. Essa análise tem como referência a Norma Regulamentadora nº 17 (NR-17) – Ergonomia, e é embasada nos levantamentos realizados por meio da Avaliação Ergonômica Preliminar (AEP) e/ou da Análise Ergonômica do Trabalho (AET).',
          'Riscos ocupacionais ergonômicos/psicossociais: Têm como objetivo identificar os perigos e riscos que possam impactar a saúde psicossocial dos funcionários, considerando a relação com as atividades desenvolvidas. A análise busca destacar pontos de atenção e prever medidas preventivas para o tratamento adequado do tema, conforme previsto na Norma Regulamentadora nº 1 (NR-1). O mapeamento abrange fatores psicossociais presentes no ambiente de trabalho, como problemas na concepção, organização e gestão das atividades laborais, que podem gerar efeitos adversos à saúde dos trabalhadores nos âmbitos psicológico, físico e social.',
          'Riscos ocupacionais acidentes/mecânicos: Referem-se à avaliação das condições estruturais dos ambientes físicos de trabalho, com foco na identificação de elementos que possam representar riscos diretos ou indiretos de acidentes para funcionários e colaboradores. Essa análise abrange aspectos como integridade das instalações, presença de superfícies escorregadias, desníveis, mobiliário inadequado, equipamentos mal posicionados, entre outros fatores que possam comprometer a segurança no ambiente laboral.'
        ]}
      />

      <Paragraph>
        Nota 1: Os produtos químicos existentes nas áreas administrativas do Banco Bradesco, são de baixa complexidade e
        restritos ao contato direto dos colaboradores das empresas terceirizadas de limpeza, e evidenciados de forma
        mais detalhadas no PGR da empresa contratada;
      </Paragraph>

      <Paragraph>
        Nota 2: Os agentes biológicos ficam restritos ao contato direto dos colaboradores das empresas terceirizadas de
        limpeza, que no momento da limpeza dos sanitários ficam expostos a esses agentes, mas considerando a utilização
        de EPI (luvas e botas) e o baixo nível de complexidade o risco de contaminação se torna irrelevante, também
        sendo detalhado no PGR da empresa contratada.
      </Paragraph>

      <SubSubHeading>5.2.1. Classificação dos Riscos Biológicos</SubSubHeading>
      <Paragraph>
        De acordo com a NR 32, em seu item 32.2.1.1, são considerados riscos biológicos os microrganismos, geneticamente
        modificados ou não; as culturas de células; os parasitas; as toxinas e os príons.
      </Paragraph>

      <Paragraph>A transmissão do agente biológico até o hospedeiro pode ocorrer de duas formas:</Paragraph>
      <Bullets
        items={[
          'Direta: transmissão do agente biológico sem a intermediação de veículos ou vetores.',
          'Indireta: transmissão do agente biológico por meio de veículos ou vetores.'
        ]}
      />

      <Paragraph>
        Ainda sobre a NR 32, no Anexo I, determina a classificação dos agentes biológicos, assim os critérios abaixo
        poderão serem avaliados na matriz de risco no item severidade:
      </Paragraph>
      <Bullets
        items={[
          'Classe de risco 1: baixo risco individual para o trabalhador e para a coletividade, com baixa probabilidade de causar doença ao ser humano.',
          'Classe de risco 2: risco individual moderado para o trabalhador e com baixa probabilidade de disseminação para a coletividade. Podem causar doenças ao ser humano, para as quais existem meios eficazes de profilaxia ou tratamento.',
          'Classe de risco 3: risco individual elevado para o trabalhador e com probabilidade de disseminação para a coletividade. Podem causar doenças e infecções graves ao ser humano, para as quais nem sempre existem meios eficazes de profilaxia ou tratamento.',
          'Classe de risco 4: risco individual elevado para o trabalhador e com probabilidade elevada de disseminação para a coletividade. Apresenta grande poder de transmissibilidade de um indivíduo a outro. Podem causar doenças graves ao ser humano, para as quais não existem meios eficazes de profilaxia ou tratamento.'
        ]}
      />

      <SubHeading id="5.3">5.3. Avaliação dos Riscos e Exposição dos Trabalhadores: Setores (GHE)</SubHeading>
      <Paragraph>
        A avaliação dos riscos ocupacionais e da exposição dos trabalhadores foi realizada com base na estrutura
        organizacional formalizada pelo Departamento de Pessoas, Cultura & Performance, que define os setores e
        respectivos cargos/funções presentes em cada dependência do Banco Bradesco S.A.. Essa organização permite uma
        análise sistemática e precisa dos fatores de risco associados às atividades desenvolvidas em cada local de
        trabalho.
      </Paragraph>

      <Paragraph>
        Para fins de gestão de riscos e controle de exposição, cada setor foi considerado como um Grupo Homogêneo de
        Exposição (GHE). Essa abordagem se justifica pela similaridade das condições ambientais, operacionais e
        organizacionais que caracterizam os postos de trabalho dentro de um mesmo setor. Os trabalhadores alocados
        nesses setores desempenham funções que, embora possam variar em nomenclatura ou atribuições específicas,
        compartilham níveis equivalentes de exposição aos agentes físicos, químicos, biológicos, ergonômicos e mecânicos
        presentes no ambiente.
      </Paragraph>

      <Paragraph>
        A definição dos GHEs por setor permite otimizar a avaliação dos riscos, concentrando esforços na caracterização
        coletiva da exposição, sem comprometer a representatividade dos dados. Essa metodologia também facilita a
        implementação de medidas preventivas e corretivas, uma vez que as ações podem ser direcionadas de forma
        integrada para todos os cargos que compõem o GHE.
      </Paragraph>

      <Paragraph>A avaliação contemplou os seguintes aspectos:</Paragraph>
      <Bullets
        items={[
          'Identificação dos agentes de risco presentes em cada setor;',
          'Análise das atividades realizadas e dos processos envolvidos;',
          'Estimativa da intensidade, frequência e duração da exposição;',
          'Verificação das medidas de controle existentes (iluminação, climatização do ambiente);',
          'Proposição de ações de melhoria, conforme aplicável.'
        ]}
      />

      <Paragraph>
        Essa abordagem garante maior efetividade na gestão dos riscos ocupacionais, promovendo a saúde e segurança dos
        trabalhadores de forma alinhada com os princípios da legislação vigente e das boas práticas de higiene
        ocupacional.
      </Paragraph>

      <RedTable width="100%">
        <RedRow>
          <RedHeadCell width="35%">Setor</RedHeadCell>
          <RedHeadCell>Cargo (GHE)</RedHeadCell>
        </RedRow>

        {setoresCargosGhe.map(({ setor, cargos }, index) => (
          <RedRow key={setor} striped={index % 2 === 1}>
            <RedDataCell align="left">{setor}</RedDataCell>
            <RedDataCell align="left" preLine>
              {cargos.join('\n')}
            </RedDataCell>
          </RedRow>
        ))}
      </RedTable>

      <Separator weight={8} />

      <Text size="p" align="justify">
        <Bold>OBSERVAÇÃO.:</Bold> Os GHEs dos setores e cargos observados nesta dependência segue o padrão adotado nas
        agências do Banco Bradesco, podendo, no entanto, apresentar variações pontuais de uma agência para outra,
        conforme as necessidades locais, porte da unidade e volume de operações.
      </Text>
      <Separator weight={6} />

      <Paragraph>
        As atividades de limpeza e segurança patrimonial são realizadas por empresas contratadas e são gerenciadas nos
        seus respectivos PGR&apos;s, conforme previsto no item: 1.5.8.2 da Norma Regulamentadora nº 1.
      </Paragraph>

      <Text bold size="p">
        Recursos Humanos da Dependência
      </Text>
      <Separator weight={5} />
      <Paragraph>
        Os funcionários desta dependência estão distribuídos pelos setores e cargos descritos neste documento. Estes são
        os recursos humanos considerados nas análises dos riscos ocupacionais inerentes a essas atividades.
      </Paragraph>

      <Separator weight={15} />
    </View>
  )
}

export default MetodologiaSection
